import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useDevice } from '#imports'
import { useApi } from '~/composables/common/use-api'
import { CMS_API } from '~/configs/api'
import { INSTRUCTION_SLUGS, DEPOSIT_METHOD, WITHDRAW_METHOD } from '~/constants'
import { AccountTabs } from '~/constants/account'

interface InstructMenuItem {
  title: string
  slug: string
  icon?: string
  subItems?: InstructMenuItem[]
}

const globImages: GlobImageI = import.meta.glob('~/assets/images/pages/instruction/*', { eager: true })
const iconImgUrl = '/assets/images/pages/instruction'

const QUERY_TYPE_CONTENT_TAB_MAP = {
  [DEPOSIT_METHOD.QR_PAY]: 'qrpay',
  [DEPOSIT_METHOD.IBANKING]: 'universal',
  [WITHDRAW_METHOD.IBANKING]: 'universal',
  [DEPOSIT_METHOD.PHONE_CARD]: 'card',
  [WITHDRAW_METHOD.PHONE_CARD]: 'card',
  [DEPOSIT_METHOD.MOMO]: 'momo',
  [DEPOSIT_METHOD.CRYPTO]: 'crypto',
  [WITHDRAW_METHOD.CRYPTO]: 'crypto'
}

export function useInstruction() {
  const { public: environment } = useRuntimeConfig()
  const { request } = useApi()
  const { isMobile } = useDevice()
  const router = useRouter()
  const route = useRoute()

  const brandName = environment.BRAND_NAME
  const indirectBrandName = environment.INDIRECT_BRAND_NAME
  const indirectBrand2Name = environment.INDIRECT_BRAND2_NAME

  const instructMenu: InstructMenuItem[] = [
    {
      title: 'Về chúng tôi',
      slug: INSTRUCTION_SLUGS.ABOUT_US,
      icon: getImageURL(globImages, 'people', iconImgUrl, 'png')
    },
    {
      title: 'Hướng dẫn cược bóng đá',
      slug: INSTRUCTION_SLUGS.HOW_TO_BET,
      icon: getImageURL(globImages, 'football', iconImgUrl, 'png')
    },
    {
      title: 'Hướng dẫn tạo tài khoản',
      slug: INSTRUCTION_SLUGS.HOW_TO_CREATE_ACCOUNT,
      icon: getImageURL(globImages, 'person', iconImgUrl, 'png')
    },
    {
      title: 'Hướng dẫn sử dụng',
      slug: '',
      icon: getImageURL(globImages, 'withdraw', iconImgUrl, 'png'),
      subItems: [
        {
          title: 'Hướng dẫn nạp tiền',
          slug: INSTRUCTION_SLUGS.HOW_TO_DEPOSIT
        },
        {
          title: 'Hướng dẫn rút tiền',
          slug: INSTRUCTION_SLUGS.HOW_TO_WITHDRAW
        },
        {
          title: 'Hướng dẫn chuyển ví',
          slug: INSTRUCTION_SLUGS.HOW_TO_TRANSFER
        }
      ]
    },
    {
      title: 'Tài khoản dùng thử',
      slug: INSTRUCTION_SLUGS.TRIAL_ACCOUNT,
      icon: getImageURL(globImages, 'person-plus', iconImgUrl, 'png')
    },
    {
      title: 'Câu hỏi thường gặp',
      slug: '',
      icon: getImageURL(globImages, 'question', iconImgUrl, 'png'),
      subItems: [
        {
          title: `${indirectBrandName}` + ' là gì?',
          slug: INSTRUCTION_SLUGS.WHAT_IS_BONG88
        },
        {
          title: `${indirectBrand2Name}` + ' là gì?',
          slug: INSTRUCTION_SLUGS.WHAT_IS_BONGK
        },
        {
          title: `Mối quan hệ ${brandName} và ${indirectBrandName}`,
          slug: INSTRUCTION_SLUGS.RELATIONSHIP_WITH_BONG88
        },
        {
          title: `Mối quan hệ ${brandName} và ${indirectBrand2Name}`,
          slug: INSTRUCTION_SLUGS.RELATIONSHIP_WITH_BONGK
        },
        {
          title: 'Cược trung gian là gì?',
          slug: INSTRUCTION_SLUGS.INTERMEDIATE_BETS
        },
        {
          title: 'Nhà cái ' + `${brandName}` + ' có uy tín không?',
          slug: INSTRUCTION_SLUGS.IS_REPUTABLE
        },
        {
          title: 'Hỗ trợ 24/7 như thế nào?',
          slug: INSTRUCTION_SLUGS.SUPPORT
        }
      ]
    },
    {
      title: 'Chính sách bảo mật',
      slug: INSTRUCTION_SLUGS.PRIVACY_POLICY,
      icon: getImageURL(globImages, 'file', iconImgUrl, 'png')
    }
  ]

  const breadcrumbTitle = ref('')
  const postDetail = ref('')
  const loading = ref(false)
  const openIndices = ref<number[]>([])
  const postContentRef = ref<HTMLDivElement>()

  let menuItemSelected: InstructMenuItem | undefined
  instructMenu.forEach(item => {
    if (item.slug === route.params.slug) {
      menuItemSelected = item
    }
    if (!menuItemSelected && item.subItems) {
      const slugItem = item.subItems.find(subMenuItem => subMenuItem.slug === route.params.slug)
      if (slugItem) {
        menuItemSelected = slugItem
      }
    }
    if (menuItemSelected) {
      return false
    }
  })

  const transactionNavigation = computed(() => {
    if (route.params.slug === INSTRUCTION_SLUGS.HOW_TO_DEPOSIT) {
      return {
        text: `☘️ ☘️ ☘️ ${brandName} chúc Quý khách cược thắng lớn và gặp nhiều may mắn! ☘️ ☘️ ☘️`,
        button: 'Nạp tiền ngay'
      }
    } else if (
      typeof route.params.slug === 'string'
      && [INSTRUCTION_SLUGS.HOW_TO_WITHDRAW, INSTRUCTION_SLUGS.HOW_TO_TRANSFER].includes(route.params.slug)
    ) {
      return { button: 'Tôi đã hiểu' }
    }
    return null
  })

  onMounted(() => {
    instructMenu.forEach((el, ind) => {
      if (el.subItems && el.subItems.length > 0) {
        openIndices.value.push(ind)
      }
    })
  })

  const getPostDetailWithTabSelected = (content?: string) => {
    if (!menuItemSelected?.slug || !content) {
      return ''
    }
    const isDepositInstruction = menuItemSelected.slug === INSTRUCTION_SLUGS.HOW_TO_DEPOSIT
    const isWithdrawInstruction = menuItemSelected.slug === INSTRUCTION_SLUGS.HOW_TO_WITHDRAW
    if (!isDepositInstruction && !isWithdrawInstruction) {
      return content
    }
    const transactionMethod = route.query.type
    if (typeof transactionMethod !== 'string' || !(transactionMethod in QUERY_TYPE_CONTENT_TAB_MAP)) {
      return content
    }
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = content
    tempDiv.querySelector('.nav-link.active')?.classList.remove('active')
    tempDiv.querySelector('.tab-pane.active.show')?.classList.remove('active', 'show')
    const activeTabPaneSelector =
      `#${isDepositInstruction ? '' : 'withdraw-'}${QUERY_TYPE_CONTENT_TAB_MAP[transactionMethod]}`
    const activeNavLinkSelector = `${activeTabPaneSelector}-tab`
    tempDiv.querySelector(activeNavLinkSelector)?.classList.add('active')
    tempDiv.querySelector(activeTabPaneSelector)?.classList.add('active', 'show')
    return tempDiv.innerHTML
  }

  const fetchInstructionData = async (slug: string | undefined): Promise<void> => {
    try {
      loading.value = true
      if (typeof slug === 'string') {
        const params = isMobile ? { slug: slug + '-mb' } : { slug }
        const { data } = await request(CMS_API.POST_DETAIL, { params })

        if (data?.length) {
          postDetail.value = getPostDetailWithTabSelected(data[0]?.post.content)
        }
      }
    } finally {
      loading.value = false
    }
  }
  breadcrumbTitle.value = menuItemSelected?.title ?? ''

  const handleItemClick = (item: InstructMenuItem, ind: number): void => {
    if (item.subItems && item.subItems.length > 0) {
      if (openIndices.value.includes(ind)) {
        const pos = openIndices.value.findIndex(el => el === ind)
        openIndices.value.splice(pos, 1)
      } else {
        openIndices.value.push(ind)
      }
    }
  }

  const navigateToTransaction = () => {
    if (route.params.slug === INSTRUCTION_SLUGS.HOW_TO_TRANSFER) {
      return router.push(AccountTabs.TRANSFER_MONEY)
    }
    const activeTab = postContentRef.value?.querySelector('.tab-pane.active.show')?.id.replace('withdraw-', '')
    const transactionMethod = Object.keys(QUERY_TYPE_CONTENT_TAB_MAP)
      .find(key => QUERY_TYPE_CONTENT_TAB_MAP[key] === activeTab)
    if (!transactionMethod) {
      return
    }
    if (route.params.slug === INSTRUCTION_SLUGS.HOW_TO_DEPOSIT) {
      return router.push(`${AccountTabs.DEPOSIT}&type=${transactionMethod}`)
    }
    if (route.params.slug === INSTRUCTION_SLUGS.HOW_TO_WITHDRAW) {
      return router.push(`${AccountTabs.WITHDRAW}&type=${transactionMethod}`)
    }
  }

  return {
    instructMenu,
    handleItemClick,
    openIndices,
    breadcrumbTitle,
    postDetail,
    loading,
    postContentRef,
    fetchInstructionData,
    transactionNavigation,
    navigateToTransaction
  }
}
